import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { PosService } from '../../modules/admin/ecommerce/services/pos.service';
import { FuseConfigService } from '../../../@fuse/services/config';
import { ProductsServiceAdapter } from '../integrations/nosERP/services/products.service.adapter';
import { RegisterValidationService } from '../../modules/admin/ecommerce/services/register-validation.service';
import { CategoriesService } from '../../modules/admin/ecommerce/services/categories.service';
import { OpenRegisterComponent } from '../../modules/admin/ecommerce/pos/open-register/open-register.component';

@Injectable()
export class AuthService {
  private _authenticated: boolean = false;

  /**
   * Constructor
   */
  constructor(
    private _httpClient: HttpClient,
    private _userService: UserService,
    private posService: PosService,
    private injector: Injector,
    private configService: FuseConfigService,
    private registerValidationService: RegisterValidationService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setter & getter for access token
   */
  set access_token(token: string) {
    localStorage.setItem('access_token', token);
  }

  get access_token(): string {
    return localStorage.getItem('access_token') ?? '';
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Forgot password
   *
   * @param email
   */
  forgotPassword(email: string): Observable<any> {
    return this._httpClient.post('api/auth/forgot-password', email);
  }

  /**
   * Reset password
   *
   * @param password
   */
  resetPassword(password: string): Observable<any> {
    return this._httpClient.post('api/auth/reset-password', password);
  }

  /**
   * Sign in
   *
   * @param credentials
   */
  signIn(credentials: { email: string; password: string }): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      return throwError('User is already logged in.');
    }

    return this._httpClient
      .post('/api/login', {
        email: credentials.email,
        password: credentials.password
      })
      .pipe(
        switchMap((response: any) => {
          if (response.status === 0) {
            return throwError(response.error);
          }

          const isRestaurant = response.utilizador.empresa.id_perfil === 9;

          if (isRestaurant) {
            localStorage.setItem('isRestaurant', 'true');
          } else {
            if (response.utilizador.gerente !== 1 && !localStorage.getItem('defaultPos')) {
              throw 'Dispositivo nao configurado, entre como um gerente para configurar o dispositivo';
            }
            localStorage.setItem('isRestaurant', 'false');
          }

          this.configService.init();

          // Store the access token in the local storage
          this.access_token = response.access_token;

          // Set the authenticated flag to true
          this._authenticated = true;

          // Store the user on the user service
          this._userService.user = response.utilizador;

          const productsAdapter = this.injector.get(ProductsServiceAdapter);
          productsAdapter.fetchProducts().subscribe();

          // Save flags for signInUsingToken
          localStorage.setItem('efetuar_fecho', response.efetuar_fecho);
          localStorage.setItem('initiate_cash_register', response.initiate_cash_register);

          // Check if a new opening is required
          if ((response.efetuar_fecho || response.initiate_cash_register) && this.registerValidationService.checkIfNewOpeningRequired()) {
            // Open the non-closable register dialog
            this.registerValidationService.openRegisterDialog(OpenRegisterComponent);
          }

          // Return a new observable with the response
          return of(response);
        })
      );
  }

  /**
   * Sign in using the access token
   */
  signInUsingToken(): Observable<any> {
    // Renew token
    return this._httpClient
      .post('api/refresh-access-token', {
        token: this.access_token
      })
      .pipe(
        catchError(() =>
          // Return false
          of(false)
        ),
        switchMap((response: any) => {
          if (response.status === 0) {
            return throwError(response.error);
          }

          // Store the access token in the local storage
          this.access_token = response.access_token;

          // Set the authenticated flag to true
          this._authenticated = true;

          const productsAdapter = this.injector.get(ProductsServiceAdapter);
          productsAdapter.fetchProducts().subscribe();

          // Load the user from localStorage
          this._userService.get().subscribe(() => {
            // Check if a new opening is required
            const efetuarFecho = localStorage.getItem('efetuar_fecho');
            const initiateCashRegister = localStorage.getItem('initiate_cash_register');
            if ((efetuarFecho === '1' || initiateCashRegister === '1') && this.registerValidationService.checkIfNewOpeningRequired()) {
              // Open the non-closable register dialog
              this.registerValidationService.openRegisterDialog(OpenRegisterComponent);
            }
          });

          // Return a new observable with the response
          return of(response);
        })
      );
  }

  /**
   * Sign out
   */
  signOut(): Observable<any> {
    // Remove the access token from the local storage
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    localStorage.removeItem('isRestaurant');
    localStorage.removeItem('closingStatus');

    // Set the authenticated flag to false
    this._authenticated = false;

    // Clear the products and categories cache
    const productsAdapter = this.injector.get(ProductsServiceAdapter);
    productsAdapter.clearProductsCache();

    const categoriesService = this.injector.get(CategoriesService);
    categoriesService.clearCategoriesCache();

    // Return the observable
    return of(true);
  }

  /**
   * Sign up
   *
   * @param user
   */
  signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
    return this._httpClient.post('api/auth/sign-up', user);
  }

  /**
   * Unlock session
   *
   * @param credentials
   */
  unlockSession(credentials: { email: string; password: string }): Observable<any> {
    return this._httpClient.post('api/auth/unlock-session', credentials);
  }

  /**
   * Check the authentication status
   */
  check(): Observable<boolean> {
    // Check if the user is logged in
    if (this._authenticated) {
      return of(true);
    }

    // Check the access token availability
    if (!this.access_token || this.access_token === 'undefined') {
      localStorage.removeItem('access_token');
      return of(false);
    }

    // Check the access token expire date
    if (AuthUtils.isTokenExpired(this.access_token)) {
      return of(false);
    }

    if (this.access_token) {
      // Set authenticated flag to true since token is valid
      this._authenticated = true;

      // Load the user from localStorage
      this._userService.get().subscribe(() => {
        // Check if a new opening is required
        if (this.registerValidationService.checkIfNewOpeningRequired()) {
          // Open the non-closable register dialog
          this.registerValidationService.openRegisterDialog(OpenRegisterComponent);
        }
      });

      return of(true);
    }

    return this.signInUsingToken();
  }
}
